import { AxiosResponse } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardApi } from "../../../services/board-service-api";
import { ProjectService } from "../../../services/project-service";
import { withManagement } from "../../../shared/hoc/with-management";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import BoardLoading from "./board-loading";
import "./board-projects-index.scss";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import { Board, Project } from "./types";
import { useBoardConfig } from "./use-board";

interface PlatformProject {
  id: string;
  name: string;
}

// The board feature's landing page: a project-first index. Lists the board-enabled projects (open
// one → its work hub), the platform projects an owner can still enable, and the cross-project
// boards (which don't belong to a single project). Replaces the old workspace onboarding gate.
const BoardProjectsIndexInner: React.FC = () => {
  const { config, loaded } = useBoardConfig();
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  const [platformProjects, setPlatformProjects] = useState<PlatformProject[]>([]);
  const [enabling, setEnabling] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<boolean>(false);
  // Server-persisted state survives refreshes; the local flag gives instant feedback after a click
  // (before the next config refetch lands).
  const [justRequested, setJustRequested] = useState<boolean>(false);
  // A request that found no owner/supervisor to email — show a contact-admin hint, don't claim "sent".
  const [noRecipients, setNoRecipients] = useState<boolean>(false);
  const accessRequested: boolean = config.accessRequested || justRequested;

  useEffect(() => {
    let cancelled: boolean = false;
    void (async () => {
      try {
        const response: AxiosResponse<PlatformProject[]> = await ProjectService.GetAllProjectNames();
        if (!cancelled) setPlatformProjects((response?.data as PlatformProject[]) ?? []);
      } catch {
        if (!cancelled) setPlatformProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enabledPlatformIds: Set<string> = useMemo(
    () => new Set<string>(config.projects.map((project: Project) => project.platformProjectId ?? "").filter(Boolean)),
    [config.projects]
  );
  const enableable: PlatformProject[] = useMemo(
    () => platformProjects.filter((project: PlatformProject) => !enabledPlatformIds.has(project.id)),
    [platformProjects, enabledPlatformIds]
  );
  const crossProjectBoards: Board[] = useMemo(
    () => config.boards.filter((board: Board) => board.scope.kind === "multi_project"),
    [config.boards]
  );

  const enable = async (platform: PlatformProject): Promise<void> => {
    if (enabling) return;
    setEnabling(platform.id);
    try {
      const project: Project = await BoardApi.createProject(platform.id, platform.name);
      await BoardService.refresh({ kind: "config.changed" });
      navigate(`/${domainName}/board/${project.key}`);
    } catch {
      setEnabling(null);
    }
  };

  const requestAccess = async (): Promise<void> => {
    if (requesting || accessRequested) return;
    setRequesting(true);
    try {
      const notified: number = await BoardApi.requestAccess();
      if (notified > 0) setJustRequested(true);
      else setNoRecipients(true); // nobody to notify — server didn't persist; let them contact an admin
    } catch {
      setJustRequested(false);
    } finally {
      setRequesting(false);
    }
  };

  if (!loaded) {
    return (
      <div className="kuika-board" data-testid="kuika-board">
        <BoardLoading />
      </div>
    );
  }

  const nothingEnabled: boolean = config.projects.length === 0;

  return (
    <div className="kuika-board kuika-projects-index" data-testid="kuika-projects-index">
      <div className="kuika-projects-index__body">
        <h1 className="kuika-projects-index__title">Boards</h1>

        {/* Projects exist but the user can access none — an authorization state, not onboarding. */}
        {config.accessRestricted && (
          <div className="kuika-projects-index__notice">
            <p>You don’t have access to any project board in this workspace.</p>
            <p className="kuika-projects-index__notice-ok">
              Ask a workspace owner or supervisor to grant you access to a project.
            </p>
          </div>
        )}

        {/* Nothing set up yet (and the user can't manage) — offer to ping the owners. */}
        {!config.accessRestricted && nothingEnabled && !config.canManage && (
          <div className="kuika-projects-index__notice">
            <p>No projects are set up for work tracking yet. A workspace owner or supervisor needs to enable one.</p>
            {accessRequested ? (
              <p className="kuika-projects-index__notice-ok">
                Access requested — the workspace owners and supervisors have been emailed.
              </p>
            ) : noRecipients ? (
              <p className="kuika-projects-index__notice-warn">
                We couldn’t find a workspace owner or supervisor to notify — please contact your administrator directly.
              </p>
            ) : (
              <KuikaButton type="primary" onClick={requestAccess} loading={requesting}>
                Request access
              </KuikaButton>
            )}
          </div>
        )}

        {config.projects.length > 0 && (
          <section className="kuika-projects-index__section">
            <div className="kuika-projects-index__section-title">Projects</div>
            <div className="kuika-projects-index__grid">
              {config.projects.map((project: Project) => (
                <button
                  key={project.key}
                  type="button"
                  data-testid="boardProjectCard"
                  className="kuika-projects-index__card"
                  onClick={() => navigate(`/${domainName}/board/${project.key}`)}
                >
                  <span className="kuika-projects-index__card-dot" style={{ background: project.color }} />
                  <span className="kuika-projects-index__card-name">{project.name}</span>
                  <span className="kuika-projects-index__card-key">{project.key}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {config.canManage && enableable.length > 0 && (
          <section className="kuika-projects-index__section">
            <div className="kuika-projects-index__section-title">Enable a project</div>
            <div className="kuika-projects-index__section-sub">
              Turn on work tracking for a workspace project — it gets the default workflow and becomes a board hub.
            </div>
            <ul className="kuika-projects-index__enable-list">
              {enableable.map((platform: PlatformProject) => (
                <li key={platform.id} data-testid="boardEnableProjectRow" className="kuika-projects-index__enable-row">
                  <span className="kuika-projects-index__enable-name">{platform.name}</span>
                  <KuikaButton
                    data-testid="boardEnableProjectButton"
                    type="secondary"
                    size="sm"
                    loading={enabling === platform.id}
                    disabled={enabling !== null}
                    onClick={() => enable(platform)}
                  >
                    Enable
                  </KuikaButton>
                </li>
              ))}
            </ul>
          </section>
        )}

        {crossProjectBoards.length > 0 && (
          <section className="kuika-projects-index__section">
            <div className="kuika-projects-index__section-title">Cross-project boards</div>
            <ul className="kuika-projects-index__cross-list">
              {crossProjectBoards.map((board: Board) => (
                <li key={board.id}>
                  <button
                    type="button"
                    className="kuika-projects-index__cross-row"
                    onClick={() => navigate(buildBoardPath(domainName ?? "", board))}
                  >
                    <span className="kuika-projects-index__cross-glyph" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <span>{board.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

const BoardProjectsIndex = withManagement<{}>(BoardProjectsIndexInner);
// The unwrapped page (no management shell) is reused by the in-designer board overlay.
export { BoardProjectsIndex, BoardProjectsIndexInner };
export default BoardProjectsIndex;
